import React, { useState, ReactNode, KeyboardEvent, useId, useRef, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router';
import styled from 'styled-components';
import { Colors } from '../../../assets';

type TabProps = {
  title: string;
  pageSlug?: string;
  children: ReactNode;
};

type TabsProps = {
  tabListJustify?: 'space-between' | 'center';
  basePath?: string;
  slugParam?: string;
  children: React.ReactElement<TabProps>[];
};

const getTabIndex = (tabs: React.ReactElement<TabProps>[], currentSlug?: string) => {
  if (currentSlug) {
    const matchedIndex = tabs.findIndex((tab) => tab.props.pageSlug === currentSlug);
    if (matchedIndex >= 0) return matchedIndex;
  }

  return 0;
};

export const Tab: React.FC<TabProps> = ({ children }) => {
  return <>{children}</>;
};

export const Tabs: React.FC<TabsProps> = ({
  children,
  tabListJustify,
  basePath,
  slugParam = 'page'
}) => {
  const navigate = useNavigate();
  const params = useParams();
  const currentSlug = params[slugParam] || params['page'];

  const tabElements = React.Children.toArray(children).filter(
    React.isValidElement
  ) as React.ReactElement<TabProps>[];

  const tabKey = tabElements
    .map((tab) => `${tab.props.title}:${tab.props.pageSlug ?? ''}`)
    .join('|');

  const initialIndex = getTabIndex(tabElements, currentSlug);
  const [activeIndex, setActiveIndex] = useState<number>(initialIndex >= 0 ? initialIndex : 0);
  const [focusedIndex, setFocusedIndex] = useState<number>(initialIndex >= 0 ? initialIndex : 0);
  const instanceId = useId();
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);

  useEffect(() => {
    const btn = tabRefs.current[focusedIndex];
    btn?.focus();
  }, [focusedIndex]);

  useEffect(() => {
    const currentSlug = params[slugParam] || params['page'];
    const newIndex = getTabIndex(tabElements, currentSlug);
    setActiveIndex(newIndex);
    setFocusedIndex(newIndex);
  }, [params[slugParam], params['page'], tabKey]);

  const moveFocus = (item: number) => {
    const totalItems = tabElements.length;
    const itemToFocus = ((item % totalItems) + totalItems) % totalItems;
    setFocusedIndex(itemToFocus);
  };

  const handleTabChangeOnKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    switch (event.key) {
      case 'ArrowRight':
        event.preventDefault();
        moveFocus(focusedIndex + 1);
        break;
      case 'ArrowLeft':
        event.preventDefault();
        moveFocus(focusedIndex - 1);
        break;
      case 'Home':
        event.preventDefault();
        moveFocus(0);
        break;
      case 'End':
        event.preventDefault();
        moveFocus(tabElements.length - 1);
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        setActiveIndex(focusedIndex);
        break;
    }
  };

  return (
    <TabsWrapper>
      <TabList role="tablist" justifyContent={tabListJustify}>
        {tabElements.map((tab, index) => {
          const isActive = activeIndex === index;
          const isFocused = focusedIndex === index;
          const tabId = `${instanceId}-tab-${index}`;
          const tabPanelId = `${instanceId}-tabpanel-${index}`;
          return (
            <TabListItem key={index} role="presentation">
              <TabButton
                type="button"
                ref={(el) => (tabRefs.current[index] = el)}
                id={tabId}
                role="tab"
                aria-selected={isActive}
                aria-controls={tabPanelId}
                tabIndex={isFocused ? 0 : -1}
                active={isActive}
                onClick={() => {
                  setActiveIndex(index);
                  setFocusedIndex(index);
                  if (tab.props.pageSlug) {
                    if (basePath) {
                      navigate(`${basePath}/${tab.props.pageSlug}`);
                    } else {
                      navigate(`/protocol/${params.id}/${tab.props.pageSlug}`);
                    }
                  }
                }}
                onKeyDown={handleTabChangeOnKeyDown}>
                {tab.props.title}
              </TabButton>
            </TabListItem>
          );
        })}
      </TabList>

      {tabElements.map((tab, index) => {
        const isActive = activeIndex === index;
        const tabPanelId = `${instanceId}-tabpanel-${index}`;
        const tabId = `${instanceId}-tab-${index}`;
        return (
          <TabPanel
            key={index}
            id={tabPanelId}
            role="tabpanel"
            aria-labelledby={tabId}
            hidden={!isActive}>
            {isActive && tab.props.children}
          </TabPanel>
        );
      })}
    </TabsWrapper>
  );
};

// Styling
export const TabsWrapper = styled.div``;

export const TabList = styled.ul<{ justifyContent?: string }>`
  display: flex;
  flex-wrap: wrap;
  ${({ justifyContent }) =>
    justifyContent &&
    `
    justify-content: ${justifyContent};
  `}
  gap: 2.5rem;
  row-gap: 0;
  list-style: none;
  padding: 0 2rem;
  margin: 0;
  background-color: ${Colors.disabled};
  overflow-x: auto;
  overflow-y: hidden;
  scroll-snap-type: x mandatory;
  scroll-behavior: smooth;
`;

export const TabListItem = styled.li``;

export const TabButton = styled.button<{ active?: boolean }>`
  all: initial;
  position: relative;
  color: ${({ active }) => (active ? `${Colors.secondary}` : `${Colors.mainText}`)};
  font-family: 'Arial Nova', sans-serif;
  cursor: pointer;
  height: 50px;
  white-space: nowrap;

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    border-bottom: 4px solid ${Colors.secondary};
    width: 0;
    transition: width 0.25s ease;
  }

  &:hover,
  &:focus {
    color: ${Colors.secondary};
    outline: none;
  }

  &:focus::after,
  &:hover::after {
    width: 50%;
  }

  ${({ active }) =>
    active &&
    `
    font-weight: 600;
    &::after {
      width: 50%;
    }
  `}
`;

export const TabPanel = styled.div`
  padding-top: 2rem;
`;
